'use client';

// The dictation waveform: the full width of the composer's recording row.
//
// The microphone level is sampled ten times a second while audio is captured;
// each sample becomes one slot, newest on the right. The newest slots are bars as tall as the level; older ones fade into
// dots, so a long dictation reads as "past" on the left and "now" on the
// right. Drawn on a canvas in the element's own text colour (a design token),
// so it follows the theme; it is decorative (aria-hidden).
import type { SpeechPhase } from '@/speech/interactive/store';
import { cn } from '@/lib/utils';
import { useEffect, useRef, type FC } from 'react';

/** One slot: a 2 px bar or dot plus a 2 px gap. */
export const WAVEFORM_SLOT_PX = 4;
/** The newest share of the width drawn as bars; the rest are dots. */
export const WAVEFORM_BAR_SHARE = 0.4;
/** Below this level a slot is drawn as a dot even when it is recent. */
const QUIET = 0.02;
const MAX_SLOTS = 400;
const SAMPLE_MS = 100;
/** Phases in which the microphone is capturing, so the waveform moves. */
const CAPTURING: ReadonlySet<SpeechPhase> = new Set(['live', 'reconnecting', 'offline']);

export type WaveformMark = { kind: 'bar' | 'dot'; x: number; height: number; alpha: number };

/**
 * Where to draw each level of `history` (oldest first) in a row `width` px
 * wide and `height` px tall. Pure, so the layout is testable without a canvas.
 */
export function waveformMarks(history: number[], width: number, height: number): WaveformMark[] {
  const slots = Math.max(1, Math.floor(width / WAVEFORM_SLOT_PX));
  const shown = history.slice(-slots);
  const bars = Math.ceil(slots * WAVEFORM_BAR_SHARE);
  return shown.map((raw, index) => {
    const level = Math.max(0, Math.min(1, raw));
    const fromRight = shown.length - 1 - index;
    const x = width - (fromRight + 1) * WAVEFORM_SLOT_PX + 1;
    if (fromRight < bars && level >= QUIET)
      return { kind: 'bar', x, height: Math.max(2, Math.round(level * height)), alpha: 0.8 };
    // Older dots fade toward the left edge.
    const age = Math.min(1, fromRight / slots);
    return { kind: 'dot', x, height: 2, alpha: fromRight < bars ? 0.45 : 0.45 - 0.3 * age };
  });
}

function draw(canvas: HTMLCanvasElement, history: number[], dim: boolean) {
  const context = canvas.getContext?.('2d');
  if (!context) return;
  const ratio = globalThis.devicePixelRatio || 1;
  const width = canvas.clientWidth;
  const height = canvas.clientHeight;
  if (canvas.width !== Math.round(width * ratio)) canvas.width = Math.round(width * ratio);
  if (canvas.height !== Math.round(height * ratio)) canvas.height = Math.round(height * ratio);
  context.setTransform(ratio, 0, 0, ratio, 0, 0);
  context.clearRect(0, 0, width, height);
  context.fillStyle = getComputedStyle(canvas).color;
  const middle = height / 2;
  for (const mark of waveformMarks(history, width, height)) {
    context.globalAlpha = mark.alpha * (dim ? 0.5 : 1);
    context.beginPath();
    const top = middle - mark.height / 2;
    if (context.roundRect) context.roundRect(mark.x, top, 2, mark.height, 1);
    else context.rect(mark.x, top, 2, mark.height);
    context.fill();
  }
  context.globalAlpha = 1;
}

export const DictationWaveform: FC<{ level: number; phase: SpeechPhase; className?: string }> = ({
  level,
  phase,
  className,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const history = useRef<number[]>([]);
  const latest = useRef(level);
  latest.current = level;
  const dim = phase !== 'live';

  useEffect(() => {
    const canvas = canvasRef.current;
    if (phase === 'starting') history.current = [];
    const render = () => canvas && draw(canvas, history.current, dim);
    render();
    const observer =
      canvas && typeof ResizeObserver !== 'undefined' ? new ResizeObserver(render) : null;
    observer?.observe(canvas!);
    // Sampled on a clock, not on level changes, so silence still moves left.
    const timer = CAPTURING.has(phase)
      ? globalThis.setInterval(() => {
          history.current = [...history.current, latest.current].slice(-MAX_SLOTS);
          render();
        }, SAMPLE_MS)
      : null;
    return () => {
      observer?.disconnect();
      if (timer !== null) globalThis.clearInterval(timer);
    };
  }, [phase, dim]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      data-slot="dictation-waveform"
      data-phase={phase}
      className={cn('text-foreground block h-8 w-full min-w-0', className)}
    />
  );
};
